<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';
import { usePrimeVue } from 'primevue/config';
import { useStates } from '../router/states';
import { useRouter } from 'vue-router';
import { i18n } from '../i18n';
import es from '@/i18n/primevue/es.json';
import en from '@/i18n/primevue/en.json';
import { useLayout } from './composables/layout';
import sharedService from '../common/services/sharedService';
import SelectBranch from '../common/components/SelectBranch.vue';
import { Status } from '../common/constants/enums';
import { useToast } from 'primevue/usetoast';
import branchesService from '../views/company-settings/branches/services/branchesService';
import { useI18n } from 'vue-i18n';
import { useDefaultLogo } from '../common/composables/useDefaultLogo';
import { usePwaInstall } from '../common/composables/usePwaInstall';

const { t } = useI18n();
const toast = useToast();
const { canInstall, install: runInstall } = usePwaInstall();
const showIosInstructions = ref(false);

// Android abre el dialogo nativo sin pasos intermedios; solo iOS (que no lo soporta) recibe
// instrucciones, y si Chrome aun no considera instalable el sitio se avisa en vez de no hacer nada.
const onInstallClick = async () => {
  const outcome = await runInstall();
  if (outcome === 'ios') {
    showIosInstructions.value = true;
    return;
  }
  if (outcome === 'accepted') {
    toast.add({ severity: 'success', summary: t('appMenu.pinApp'), detail: t('appMenu.pinAppSuccess'), life: 3000 });
    return;
  }
  if (outcome === 'unavailable') {
    toast.add({ severity: 'info', summary: t('appMenu.pinApp'), detail: t('appMenu.pinAppUnavailable'), life: 6000 });
  }
};
const lang = localStorage.getItem('lang');

const selectLang = ref(lang);
const $primevue = usePrimeVue();
const { defaultLogo } = useDefaultLogo();
const {
  onLogout,
  userState,
  isEmissionPointLoad,
  companyUserStates,
  showConfigCompanyData,
  showConfigCompanyProducts,
  onSetEmissionPoint,
  onClearEmissionPoint,
  onSetHasEmissionPoints,
} = useStates();
const router = useRouter();
const menuToggle = ref(null);
const langToggle = ref(null);
const showBranchModal = ref(false);

const { setActiveMenuItem } = useLayout();

// Botón de suscripción visible para cualquier usuario autenticado (no-main lo ve en modo solo lectura).
const showSubscriptionButton = computed(() => {
  const userData = userState?.data?.value;
  return !!userData;
});

defineExpose({
  $primevue,
});

const onLoGout = () => {
  sharedService
    .logout()
    .then((response) => {
      clearSession();
    })
    .catch((error) => {
      clearSession();
    });
};

const clearSession = () => {
  onLogout();
  sessionStorage.removeItem('authToken');
  router.push('/login');
};

const onGoTo = (to) => {
  router.push(to);
  menuToggle.value.click();
  setActiveMenuItem('');
};

function findBranchByEmissionPoint(data, emissionPointId) {
  for (const item of data) {
    const emissionPoints = item.emissionPointsLst;

    if (emissionPoints) {
      for (const ep of emissionPoints) {
        if (ep.id.toString() === emissionPointId.toString()) {
          return item; // Se encontró el branch correspondiente
        }
      }
    }
  }
  return null; // No se encontró el branch
}

const initializeEmissionPoint = () => {
  sharedService.getBranches().then((response) => {
    response = response.filter((item) => item.branch.status === Status.Active);

    // Check if there are any active emission points in any branch
    const hasActiveEmissionPoints = response.some(
      (branch) => branch.emissionPointsLst && branch.emissionPointsLst.some((point) => point.status === Status.Active),
    );

    // Set the hasEmissionPoints flag
    onSetHasEmissionPoints(hasActiveEmissionPoints);

    sharedService
      .getEmissionPoint()
      .then((emisionPointResponse) => {
        let localBranch = emisionPointResponse ? findBranchByEmissionPoint(response, emisionPointResponse) : null;
        if (emisionPointResponse && localBranch && localBranch?.branch?.status == Status.Active) {
          let branchResponse = localBranch;
          let emissionPointLst = branchResponse.emissionPointsLst;
          let emisionPoint = emissionPointLst.find((ep) => ep.id.toString() === emisionPointResponse.toString());
          emisionPoint = emisionPoint.status === Status.Active ? emisionPoint : { pointCode: '00' };
          onSetEmissionPoint({ branch: branchResponse.branch, emissionPoint: emisionPoint });
          return;
        }
        onSetEmissionPoint({ branch: { establishmentCode: '00' }, emissionPoint: { pointCode: '00' } });
      })
      .catch((error) => {
        toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
        onSetEmissionPoint({ branch: { establishmentCode: '00' }, emissionPoint: { pointCode: '00' } });
      });
  });
};

onMounted(() => {
  initializeEmissionPoint();
  watch(isEmissionPointLoad, () => {
    initializeEmissionPoint();
  });

  watch(showConfigCompanyData, () => {
    toast.add({
      severity: 'info',
      summary: t('companySettings.titleMenu'),
      detail: t('companySettings.configCompany'),
      life: 3000,
    });
  });

  watch(showConfigCompanyProducts, () => {
    toast.add({
      severity: 'info',
      summary: t('products.title'),
      detail: t('products.configProducts'),
      life: 3000,
    });
  });
});

const changeLang = (lang) => {
  i18n.global.locale.value = lang;
  localStorage.setItem('lang', lang);
  selectLang.value = lang;
  const localePrimeVue = lang == 'es' ? es : en;
  const keys = Object.keys(localePrimeVue);
  keys.forEach((key) => {
    $primevue.config.locale[key] = localePrimeVue[key];
  });
  langToggle.value.click();
};

const openBranchModal = () => {
  showBranchModal.value = true;
};

const changeBranch = (event) => {
  if (!event) {
    // Modal closed without selection
    showBranchModal.value = false;
    return;
  }

  sharedService
    .setEmissionPoint(event.emissionPoint.id)
    .then((emisionPointResponse) => {
      // Quitar la clase de resaltado cuando se selecciona un punto de emisión válido
      const emissionPointSelector = document.querySelector('.emission-point-selector');
      if (emissionPointSelector) {
        emissionPointSelector.classList.remove('emission-point-highlight');
      }
      onSetEmissionPoint({ branch: event.branch, emissionPoint: event.emissionPoint });
      showBranchModal.value = false;
      toast.add({
        severity: 'success',
        summary: t('shared.success'),
        detail: t('shared.emissionPoint') + ' ' + t('shared.updated'),
        life: 3000,
      });
    })
    .catch((error) => {
      toast.add({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
      if (companyUserStates?.branch && companyUserStates?.emisionPoint) {
        onSetEmissionPoint({ branch: companyUserStates?.branch, emissionPoint: companyUserStates?.emisionPoint });
        return;
      }
      onSetEmissionPoint({ branch: { establishmentCode: '00' }, emissionPoint: { pointCode: '00' } });
    });
};

const clearEmissionPoint = (event) => {
  branchesService.clearEmissionPointAssignment(event).then((response) => {
    onClearEmissionPoint();
    showBranchModal.value = false;
    toast.add({
      severity: 'success',
      detail: t('branches.clearEmissionPointAssignmentSuccess'),
      life: 3000,
    });
  });
};
</script>

<template>
  <div class="layout-topbar-actions-end">
    <ul class="layout-topbar-items">
      <li>
        <a
          @click="openBranchModal"
          class="p-ripple select-sucursal uppercase emission-point-selector cursor-pointer"
          v-ripple>
          <span
            v-tooltip.bottom="$t('shared.emissionPoint')"
            class="mx-2 py-1"
            v-if="companyUserStates?.branch && companyUserStates?.emisionPoint">
            {{ companyUserStates?.branch.establishmentCode.toString().padStart(3, '0') }} -
            {{ companyUserStates?.emisionPoint.pointCode.toString().padStart(3, '0') }}
          </span>
        </a>

        <!-- Modal Component -->
        <SelectBranch
          :visible="showBranchModal"
          :emissionPointId="companyUserStates?.emisionPoint?.id?.toString()"
          @change="changeBranch"
          @clearEmissionPoint="clearEmissionPoint" />
      </li>
      <li>
        <a
          ref="langToggle"
          v-styleclass="{
            selector: '@next',
            enterClass: 'hidden',
            enterActiveClass: 'px-scalein',
            leaveToClass: 'hidden',
            leaveActiveClass: 'px-fadeout',
            hideOnOutsideClick: true,
          }"
          class="p-ripple"
          v-ripple>
          <i v-tooltip.bottom="$t('shared.changeLanguage')" class="pi pi-language"></i>
        </a>
        <div class="hidden">
          <ul class="list-none p-0 m-0">
            <li :class="selectLang == 'es' ? 'disable-link' : ''">
              <a
                @click="changeLang('es')"
                class="p-ripple cursor-pointer flex align-items-center py-2 hover:surface-hover transition-duration-150 transition-all px-3 py-2"
                v-ripple>
                <span>{{ $t('lang.spanish') }} </span>
              </a>
            </li>
            <li :class="selectLang == 'en' ? 'disable-link' : ''">
              <a
                @click="changeLang('en')"
                class="p-ripple cursor-pointer flex align-items-center py-2 hover:surface-hover transition-duration-150 transition-all px-3 py-2"
                v-ripple>
                <span>{{ $t('lang.english') }}</span>
              </a>
            </li>
          </ul>
        </div>
      </li>
      <li class="profile-li">
        <a
          id="menu-toggle"
          ref="menuToggle"
          v-styleclass="{
            selector: '.profile-menu-panel',
            enterClass: 'hidden',
            enterActiveClass: 'px-scalein',
            leaveToClass: 'hidden',
            leaveActiveClass: 'px-fadeout',
            hideOnOutsideClick: true,
          }"
          class="p-ripple"
          v-ripple>
          <img
            v-tooltip.bottom="$t('shared.profile')"
            class="profile-image border-circle shadow-4 w-3rem h-3rem"
            :src="userState?.data?.value?.photo || defaultLogo"
            alt="ultima-footer-logo" />
        </a>
        <div class="hidden profile-menu-panel">
          <div class="flex flex-wrap">
            <div class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2" rounded icon="pi pi-users" @click="onGoTo('/users')"></Button>
              <span>{{ $t('appMenu.users') }} </span>
            </div>
            <div class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2" severity="success" rounded icon="pi pi-palette" @click="onGoTo('/themes')"></Button>
              <span>{{ $t('appMenu.themes') }}</span>
            </div>
            <div class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2" severity="warning" rounded icon="pi pi-user" @click="onGoTo('/profile')"></Button>
              <span>{{ $t('profile.titleMenu') }}</span>
            </div>
            <div class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2" severity="help" rounded icon="pi pi-question" @click="onGoTo('/help')"></Button>
              <span>{{ $t('appMenu.help') }}</span>
            </div>
            <div v-if="showSubscriptionButton" class="w-4 flex flex-column align-items-center p-3">
              <Button
                class="mb-2"
                severity="info"
                rounded
                icon="pi pi-credit-card"
                @click="onGoTo('/subscription-plans')"></Button>
              <span>{{ $t('subscription.plansMenu') }}</span>
            </div>
            <!-- <div class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2" style="background-color: #21618c; color: white" rounded icon="pi pi-cog"></Button>
              <span>{{ $t('appMenu.configuration') }}</span>
            </div> -->
            <div class="w-4 flex flex-column align-items-center p-3">
              <Button
                class="mb-2"
                style="background-color: #dc3545; color: white"
                rounded
                icon="pi pi-power-off"
                @click="onLoGout()"></Button>
              <span>{{ $t('shared.signOff') }}</span>
            </div>
            <!-- Anclar a la pantalla de inicio o escritorio: en cualquier plataforma mientras la app no este instalada. -->
            <div v-if="canInstall" class="w-4 flex flex-column align-items-center p-3">
              <Button class="mb-2 p-button-icon-only pwa-pin-button" rounded @click="onInstallClick()">
                <img src="/icons/pin-logo.png" alt="" class="pwa-pin-logo" />
              </Button>
              <span>{{ $t('appMenu.pinApp') }}</span>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>

  <!-- iOS no expone un dialogo de instalacion: solo queda guiar al usuario por el menu Compartir. -->
  <Dialog
    v-model:visible="showIosInstructions"
    :header="$t('appMenu.pinApp')"
    :modal="true"
    :style="{ width: '400px' }"
    :breakpoints="{ '575px': '92vw' }">
    <div class="flex flex-column gap-3">
      <p class="m-0 line-height-3">{{ $t('appMenu.pinAppIosIntro') }}</p>
      <ol class="m-0 pl-4 line-height-3">
        <li class="mb-2">
          {{ $t('appMenu.pinAppIosStep1') }}
          <i class="pi pi-upload ml-1" style="vertical-align: middle"></i>
        </li>
        <li class="mb-2">
          {{ $t('appMenu.pinAppIosStep2') }}
          <i class="pi pi-plus-circle ml-1" style="vertical-align: middle"></i>
        </li>
        <li>{{ $t('appMenu.pinAppIosStep3') }}</li>
      </ol>
    </div>
    <template #footer>
      <Button :label="$t('shared.understood')" @click="showIosInstructions = false" />
    </template>
  </Dialog>
</template>
<style>
.list-language {
  position: absolute;
  padding-top: 5px;
  padding-bottom: 5px;
  bottom: 20px;
  left: 0;
  width: 100%;
  background-color: var(--surface-overlay) !important;
  z-index: 1;
  border-radius: var(--border-radius);
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.2);
}
.select-sucursal {
  width: auto !important;
  user-select: none !important;
  border-radius: 10px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  color: var(--topbar-item-text-color) !important;
  max-height: 90px !important;
  height: auto !important;
  min-height: 2.5rem;
}

.pwa-pin-button {
  background-color: #000000 !important;
  border-color: #000000 !important;
  /* El tema da 0.571rem de padding a los icon-only; aqui estorba porque el logo debe llenar
     el circulo. Se anula y se fija el mismo 2.25rem de ancho para no desalinear la fila. */
  width: 2.25rem !important;
  height: 2.25rem !important;
  padding: 0 !important;
  overflow: hidden;
}

.pwa-pin-logo {
  width: 92%;
  height: 92%;
  object-fit: contain;
}

.profile-image {
  object-fit: cover;
  width: 100%;
  height: 100%;
  /* Aro y fondo claros: la miniatura no se pierde contra el fondo de la barra superior. */
  background-color: #fff;
  border: 2px solid #fff;
}

.profile-li {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
}

@media screen and (max-width: 991px) {
  .profile-li {
    margin-left: auto;
  }
}
</style>
